import { PopoverBase } from "@hopper-ui/components";
import classNames from "classnames";
import { Fragment, useLayoutEffect, useState, type ReactNode, type Ref, type RefObject } from "react";
import { Header, Menu, MenuItem, MenuSection, Text, VisuallyHidden, type MenuProps } from "react-aria-components";
import { findMatchRanges } from "./textMatch.ts";
import "./ComposerTray.css";

// The trays that open above the input bar ("+" and reasoning effort) share one surface, header and row.

export const COMPOSER_TRAY_OFFSET = 4;

// React Aria sizes a menu popover from its trigger, the "+"; the tray follows the bar's width instead, also while the panel resizes.
export function useComposerTrayWidth(ref: RefObject<HTMLElement | null>, isOpen: boolean) {
    const [width, setWidth] = useState<number>();

    useLayoutEffect(() => {
        const element = ref.current;
        if (!isOpen || !element) {
            return;
        }
        setWidth(element.getBoundingClientRect().width);
        const observer = new ResizeObserver(([entry]) => setWidth(entry.borderBoxSize[0].inlineSize));
        observer.observe(element);

        return () => observer.disconnect();
    }, [isOpen, ref]);

    return width;
}

interface ComposerTrayPopoverProps {
    // The bar the tray opens above; its gap is measured from the bar, not from the button that opens it.
    anchorRef: RefObject<HTMLElement | null>;
    placement: "top" | "top end";
    offset: number;
    inlineSize?: number;
    className?: string;
    popoverRef?: Ref<HTMLElement>;
    // Set when the tray opens without a trigger (over a typed "/"): open state and dismissal come from outside, and focus stays in the field.
    standalone?: { isOpen: boolean; onOpenChange: (isOpen: boolean) => void };
    children: ReactNode;
}

export function ComposerTrayPopover({ anchorRef, placement, offset, inlineSize, className, popoverRef, standalone, children }: ComposerTrayPopoverProps) {
    return (
        <PopoverBase
            ref={popoverRef}
            triggerRef={anchorRef}
            {...(standalone ? { ...standalone, isNonModal: true } : {})}
            placement={placement}
            offset={offset}
            containerPadding={0}
            className={classNames("wai-composer-tray", className)}
            style={inlineSize === undefined ? undefined : { inlineSize }}
        >
            {children}
        </PopoverBase>
    );
}

export function ComposerTrayMenu<T extends object>({ className, ...props }: MenuProps<T> & { className?: string }) {
    return <Menu {...props} className={classNames("wai-composer-tray-menu", className)} />;
}

export function ComposerTraySection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <MenuSection className="wai-composer-tray-section">
            <Header className="wai-composer-tray-header">{title}</Header>
            {children}
        </MenuSection>
    );
}

interface ComposerTrayItemProps {
    id: string;
    name: string;
    icon?: ReactNode;
    description?: string;
    // The right column: a check, "Connect" or "Off". Screen readers hear statusLabel after the name.
    status?: ReactNode;
    statusLabel?: string;
    isQuiet?: boolean;
    // Opens in a new tab and closes the tray.
    href?: string;
    // The row the keyboard is on while focus stays in the input.
    isActive?: boolean;
    // The filter text; its matches in the name are bold.
    match?: string;
    // Fires on real pointer movement only: a tray that opens under a resting pointer must not take that row from the keyboard.
    onPointerMove?: () => void;
}

export function MatchedName({ name, match }: { name: string; match?: string }) {
    const ranges = match ? findMatchRanges(name, match) : [];
    if (ranges.length === 0) {
        return name;
    }

    let cursor = 0;
    const parts: ReactNode[] = [];
    for (const [start, end] of ranges) {
        parts.push(<Fragment key={`t${start}`}>{name.slice(cursor, start)}</Fragment>);
        parts.push(<mark key={`m${start}`} className="wai-composer-tray-item-match">{name.slice(start, end)}</mark>);
        cursor = end;
    }
    parts.push(<Fragment key="tail">{name.slice(cursor)}</Fragment>);

    return parts;
}

export function ComposerTrayItem({ id, name, icon, description, status, statusLabel, isQuiet, href, isActive, match, onPointerMove }: ComposerTrayItemProps) {
    return (
        <MenuItem
            id={id}
            textValue={name}
            className={classNames("wai-composer-tray-item", { "wai-composer-tray-item--active": isActive })}
            onPointerMove={onPointerMove}
            {...(href ? { href, target: "_blank", rel: "noopener" } : {})}
        >
            <span className="wai-composer-tray-item-icon" aria-hidden>{icon}</span>
            <Text slot="label" className={isQuiet ? "wai-composer-tray-item-quiet" : "wai-composer-tray-item-name"}>
                <MatchedName name={name} match={match} />
                {statusLabel && <VisuallyHidden>, {statusLabel}</VisuallyHidden>}
            </Text>
            {/* The title shows the whole line on hover when the single-line row truncates it. */}
            {description && <Text slot="description" title={description} className="wai-composer-tray-item-description">{description}</Text>}
            {/* Only rows that can show a status reserve its column, so the description can run to the row's edge. */}
            {status !== undefined && <span className="wai-composer-tray-item-status" aria-hidden>{status}</span>}
        </MenuItem>
    );
}
